import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Search, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const isSuper = user?.role === "super_admin";

  const load = async (q = "") => { const { data } = await api.get("/admin/users", { params: q ? { search: q } : {} }); setUsers(data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const toggleStatus = async (u) => {
    try {
      if (u.status === "deactivated") await api.post(`/admin/users/${u.id}/activate`);
      else await api.post(`/admin/users/${u.id}/deactivate`);
      toast.success("User updated."); load(search);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <PageHeader eyebrow="Support" title="Users" subtitle="Search families and manage account access. Click a row to view engagement." />
      <div className="relative mb-4 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-kid-muted" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(search)}
               placeholder="Search by email or name…" className="pl-9 h-11 rounded-xl bg-white" data-testid="admin-users-search" />
      </div>
      <div className="kid-card overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-kid-mist text-kid-muted text-left"><tr>
            <th className="px-5 py-3 font-poppins font-semibold">Name</th><th className="px-5 py-3">Email</th>
            <th className="px-5 py-3">Plan</th><th className="px-5 py-3">Roadmap</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} onClick={() => navigate(`/ops/users/${u.id}`)}
                  className="border-t border-kid-line cursor-pointer hover:bg-kid-mist/60 transition-colors" data-testid="admin-user-row">
                <td className="px-5 py-3 font-medium text-kid-navy">{u.first_name}</td>
                <td className="px-5 py-3">{u.email}</td>
                <td className="px-5 py-3">{u.plan_key === "family_path" ? "Family Path" : "Free"}</td>
                <td className="px-5 py-3">{u.has_roadmap ? "Yes" : "—"}</td>
                <td className="px-5 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${u.status === "deactivated" ? "bg-kid-coral/12 text-kid-coral" : "bg-kid-green/15 text-[#3f9d5c]"}`}>{u.status}</span></td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-3">
                    {isSuper && u.role === "parent" && (
                      <button onClick={(ev) => { ev.stopPropagation(); toggleStatus(u); }} className="text-kid-teal font-semibold" data-testid="admin-user-toggle">{u.status === "deactivated" ? "Activate" : "Deactivate"}</button>
                    )}
                    <ChevronRight size={16} className="text-kid-muted" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
